import React, { useState } from "react";
import { SovosBox, SovosButton, SovosTypography } from "mosaic-react";
import { SovosAIIcon } from "mosaic-react-platform";
import { AI_BRAND_GRADIENT_PINK } from "mosaic-react-platform/shared/aiBrandGradient";
import AiConfidencePill from "./AiConfidencePill";

const ColumnMappingAiResultBanner = ({
    suggestionCount,
    suggestions = [],
    onReviewSuggestion,
    i18n = (strings) => (Array.isArray(strings) ? strings.join("") : strings),
}) => {
    const [expanded, setExpanded] = useState(false);

    if (!suggestionCount) return null;

    const mappingLabel = suggestionCount === 1 ? i18n`column mapping` : i18n`column mappings`;

    return (
        <SovosBox
            sx={{
                bgcolor: `${AI_BRAND_GRADIENT_PINK}14`,
                borderRadius: 1,
                display: "flex",
                flexDirection: "column",
                mb: 2,
                overflow: "hidden",
            }}
        >
            <SovosBox
                sx={{
                    alignItems: "center",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.5,
                }}
            >
                <SovosBox sx={{ alignItems: "center", display: "flex", gap: 1.5, minWidth: 0 }}>
                    <SovosBox sx={{ alignItems: "center", display: "flex", "& svg": { height: 24, width: 24 } }}>
                        <SovosAIIcon />
                    </SovosBox>
                    <SovosTypography variant="body2">
                        {i18n`Sovi AI has suggested ${suggestionCount} ${mappingLabel} with high confidence scores (>80%). You can review these suggestions and manually adjust any mapping as needed.`}
                    </SovosTypography>
                </SovosBox>
                <SovosButton
                    size="small"
                    variant="text"
                    onClick={() => setExpanded((visible) => !visible)}
                    sx={{ fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", whiteSpace: "nowrap" }}
                >
                    {i18n`Review suggestions`}
                </SovosButton>
            </SovosBox>
            {expanded && (
                <SovosBox
                    sx={{
                        bgcolor: "background.paper",
                        borderTop: "1px solid",
                        borderColor: "divider",
                        maxHeight: 220,
                        overflowY: "auto",
                    }}
                >
                    <SovosBox
                        sx={{
                            alignItems: "center",
                            color: "text.secondary",
                            display: "grid",
                            fontSize: 12,
                            fontWeight: 600,
                            gridTemplateColumns: "minmax(140px, 1fr) minmax(140px, 1fr) 96px",
                            px: 2,
                            py: 0.75,
                        }}
                    >
                        <span>{i18n`CSV column`}</span>
                        <span>{i18n`Map to`}</span>
                        <span>{i18n`Confidence`}</span>
                    </SovosBox>
                    {suggestions.map((suggestion) => (
                        <SovosBox
                            key={suggestion.column}
                            component="button"
                            onClick={() => onReviewSuggestion?.(suggestion.column)}
                            sx={{
                                alignItems: "center",
                                bgcolor: "transparent",
                                border: 0,
                                borderTop: "1px solid",
                                borderColor: "divider",
                                cursor: "pointer",
                                display: "grid",
                                fontFamily: "inherit",
                                gridTemplateColumns: "minmax(140px, 1fr) minmax(140px, 1fr) 96px",
                                px: 2,
                                py: 0.75,
                                textAlign: "left",
                                width: "100%",
                                "&:hover": { bgcolor: "action.hover" },
                            }}
                        >
                            <SovosTypography variant="caption" noWrap>{suggestion.columnLabel || suggestion.column}</SovosTypography>
                            <SovosTypography variant="caption" color="primary.main" noWrap>{suggestion.targetLabel}</SovosTypography>
                            <AiConfidencePill score={suggestion.score} />
                        </SovosBox>
                    ))}
                </SovosBox>
            )}
        </SovosBox>
    );
};

export default ColumnMappingAiResultBanner;
